import ApiService from './apiService';
import { Snag } from '../shared/types/snag';

const STATUS_MAP: Record<string, Snag['status']> = {
  NEW: 'New',
  ACTIVE: 'Assigned',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Resolved',
  BLOCKED: 'Under Review',
};

const PRIORITY_MAP: Record<string, Snag['priority']> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

const API_STATUS_MAP: Record<Snag['status'], string> = {
  New: 'NEW',
  Assigned: 'ACTIVE',
  'In Progress': 'IN_PROGRESS',
  'Under Review': 'BLOCKED',
  Resolved: 'COMPLETED',
  Closed: 'COMPLETED',
};

const normalizeSnag = (snag: any): Snag => ({
  id: snag.id,
  title: snag.title,
  description: snag.description,
  priority: PRIORITY_MAP[String(snag.priority || '').toUpperCase()] || 'Medium',
  status: STATUS_MAP[String(snag.status || '').toUpperCase()] || 'New',
  assignedTo: snag.assignedTo || {
    id: 'unassigned',
    name: 'Unassigned',
    trade: 'General',
  },
  location: typeof snag.location === 'object'
    ? snag.location
    : { area: snag.location || 'Unspecified area' },
  createdBy: snag.createdBy || {
    id: 'system',
    name: 'System',
  },
  createdDate: snag.createdDate || snag.createdAt || new Date().toISOString(),
  dueDate: snag.dueDate,
  resolvedDate: snag.resolvedDate || snag.resolvedAt,
  tags: snag.tags || [],
  photos: snag.photos || [],
  comments: snag.comments || [],
  attachments: snag.attachments || [],
});

/**
 * Snag Tracker Service
 * Handles all snag-related API operations
 */
export const SnagTrackerService = {
  getSnags: async (filters?: {
    search?: string;
    priority?: string[];
    status?: string[];
    assignedTo?: string[];
    location?: string[];
    tags?: string[];
    dateRange?: { start?: string; end?: string };
  }) => {
    const response = await ApiService.get<any>('/snags', { params: filters });
    const snags = Array.isArray(response) ? response : response?.snags || [];
    return { snags: snags.map(normalizeSnag) };
  },

  getSnag: async (id: string) => {
    const response = await ApiService.get<any>(`/snags/${id}`);
    return normalizeSnag(response);
  },

  createSnag: async (snag: {
    title: string;
    description: string;
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    assignedTo?: { id: string; name: string };
    location?: string;
    tags?: string[];
    dueDate?: string;
    projectId?: string;
    siteId?: string;
  }) => {
    const response = await ApiService.post<any>('/snags', snag);
    return normalizeSnag(response);
  },

  updateSnag: async (id: string, updates: Partial<Snag>) => {
    const response = await ApiService.put<any>(`/snags/${id}`, updates);
    return normalizeSnag(response);
  },

  updateSnagStatus: async (
    id: string,
    status: 'New' | 'Assigned' | 'In Progress' | 'Under Review' | 'Resolved' | 'Closed'
  ) => {
    const response = await ApiService.put<any>(`/snags/${id}/status`, {
      status: API_STATUS_MAP[status],
    });
    return normalizeSnag(response);
  },

  deleteSnag: async (id: string) => {
    return ApiService.delete(`/snags/${id}`);
  },

  addComment: async (snagId: string, comment: string) => {
    return ApiService.post(`/snags/${snagId}/comments`, { comment });
  },

  getComments: async (snagId: string) => {
    const response = await ApiService.get<any>(`/snags/${snagId}/comments`);
    return Array.isArray(response) ? response : response?.comments || [];
  },

  assignSnag: async (snagId: string, userId: string) => {
    return ApiService.put(`/snags/${snagId}`, { assignedTo: userId });
  },

  bulkUpdateSnags: async (snagIds: string[], updates: Partial<Snag>) => {
    return ApiService.post('/snags/bulk-update', {
      ids: snagIds,
      updates,
    });
  },

  exportSnags: async (format: 'pdf' | 'excel' | 'csv', filters?: any) => {
    return ApiService.get(`/snags/export/${format}`, {
      params: filters,
      responseType: 'blob',
    });
  },
};

export default SnagTrackerService;
